import keycloak from '@auth/keycloak';
import { CapacitorHttp, HttpResponse } from '@capacitor/core';
import { API_ENDPOINT, API_ENDPOINTS } from '@shared/constants/api-endpoints';
import { LanguageCode } from '@shared/constants/language';
import { generateURL } from '@shared/helpers/url-params-formatter.helper';
import { ErrorDTO } from '@shared/models/ErrorDTO.model';
import { selectUserPreferences } from '@shared/store/app/app.selectors';
import { isCapacitorNative } from '@utils/capacitor-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

type RequestOptions = {
  method?: string;
  headers?: Headers;
  body?: string;
  signal?: AbortSignal;
};

export type GetRequestOptions = Pick<PostPutRequestOptions, 'pathParams' | 'queryParams' | 'shouldAbort'>;
export type UseFetchResponse<T> = { data: T; rawResponse: Response };

export type PostPutRequestOptions = {
  payload: unknown;
  pathParams?: Record<string, string | number>;
  queryParams?: Record<string, string | number>;
  shouldAbort?: boolean;
};

type UseFetchResult<T> = {
  loading: boolean;
  error: ErrorDTO | null;
  response: T | null;
  get: (args?: GetRequestOptions) => Promise<UseFetchResponse<T>>;
  post: (args: PostPutRequestOptions) => Promise<UseFetchResponse<T>>;
  put: (args: PostPutRequestOptions) => Promise<UseFetchResponse<T>>;
};

const UNKNOWN_ERROR_PAYLOAD: ErrorDTO = { status: 500, code: 'UNKNOWN_ERROR', message: '', details: [] };

export const useFetch = <T = unknown>(endpoint: API_ENDPOINT): UseFetchResult<T> => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorDTO | null>(null);
  const [response, setResponse] = useState<T | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const userLang = useSelector(selectUserPreferences)?.language.value || LanguageCode.GR;

  // Clean up on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const makeRequestCapacitor = async (url: URL, options: RequestOptions = {}) => {
    const nativeResp: HttpResponse = await CapacitorHttp.request({
      method: options.method,
      url: url.toString(),
      headers: options.headers ? Object.fromEntries(options.headers.entries()) : {},
      data: options.body
    });

    // HttpResponse has `status` and `data`
    if (nativeResp.status! < 200 || nativeResp.status! >= 300) {
      let errorBody;
      try {
        // Try to parse error response body if it exists
        errorBody = typeof nativeResp.data === 'string' ? JSON.parse(nativeResp.data) : nativeResp.data;
      } catch {
        errorBody = { message: 'Failed to parse error response', code: 'PARSE_ERROR', details: [] };
      }

      throw { status: nativeResp.status, ...errorBody, rawResponse: nativeResp };
    }

    return nativeResp.data;
  };

  const makeRequestWeb = async (
    url: URL,
    options: RequestOptions = {},
    abortController: AbortController,
    shouldAbort = true
  ) => {
    const res = await fetch(url, { ...options, signal: shouldAbort ? abortController.signal : undefined });

    // Propagate the error to the catch block, in order each caller to handle as needed per case
    if (!res.ok) {
      let errorBody;
      try {
        errorBody = await res.json();
      } catch {
        errorBody = { message: 'Failed to parse error response', code: 'PARSE_ERROR', details: [] };
      }

      throw { status: res.status, ...errorBody, rawResponse: res };
    }

    const data = await res.json();
    return data;
  };

  const makeRequest = useCallback(async (url: URL, options: RequestOptions = {}, shouldAbort = true) => {
    if (shouldAbort) {
      setLoading(true);
      setError(null);
      setResponse(null);
    }

    if (shouldAbort && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = shouldAbort ? abortController : null;

    try {
      let data;
      if (isCapacitorNative()) {
        data = await makeRequestCapacitor(url, options);
      } else {
        data = await makeRequestWeb(url, options, abortController, shouldAbort);
      }

      if (shouldAbort) setResponse(data);
      setError(null); // reset error state on successful response
      return { data, rawResponse: data };
    } catch (err: unknown) {
      const { status, code, message, details } = err as ErrorDTO;
      if (status && code && message && details) {
        setError(err as ErrorDTO);
      } else {
        console.error('Error in useFetch:', err);
        setError(UNKNOWN_ERROR_PAYLOAD);
      }

      throw err; // re-throw the error to be handled by the caller
    } finally {
      if (shouldAbort) setLoading(false);
    }
  }, []);

  const getHeaders = useCallback(async () => {
    // Ensure token is fresh before making request
    try {
      await keycloak.updateToken(5); // Refresh if token will expire within 5 seconds
    } catch (error) {
      console.error('Failed to refresh token in useFetch:', error);
      // Token refresh failed, but continue with existing token
      // The API call will likely fail with 401, which is expected behavior
    }

    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Authorization', `Bearer ${keycloak.token}`);

    return headers;
  }, []);

  const getDefaultQueryParams = useCallback(() => ({ lang: userLang }), [userLang]);

  const get = useCallback(
    async (options?: GetRequestOptions) => {
      const { pathParams, queryParams, shouldAbort = true } = options || {};
      const params = { ...getDefaultQueryParams(), ...queryParams };
      const url = generateURL(API_ENDPOINTS[endpoint], pathParams, params);
      const headers = await getHeaders();
      return await makeRequest(url, { method: 'GET', headers }, shouldAbort);
    },
    [makeRequest, getHeaders, endpoint, getDefaultQueryParams]
  );

  const post = useCallback(
    async (options: PostPutRequestOptions) => {
      const { payload, pathParams, queryParams, shouldAbort = true } = options;
      const url = generateURL(API_ENDPOINTS[endpoint], pathParams, queryParams);
      const headers = await getHeaders();
      return makeRequest(url, { method: 'POST', headers, body: JSON.stringify(payload) }, shouldAbort);
    },
    [endpoint, makeRequest, getHeaders]
  );

  const put = useCallback(
    async (options: PostPutRequestOptions) => {
      const { payload, pathParams, queryParams, shouldAbort = true } = options;
      const url = generateURL(API_ENDPOINTS[endpoint], pathParams, queryParams);
      const headers = await getHeaders();
      return makeRequest(url, { method: 'PUT', headers, body: JSON.stringify(payload) }, shouldAbort);
    },
    [endpoint, makeRequest, getHeaders]
  );

  return { loading, error, response, get, post, put };
};
